(ns minorodata.users.api
  (:require [clojure.string]
            [minorodata.app.api-config :refer [AUTH_API_HOST]]
            [minorodata.users.model :refer [->client-data ->client-roles
                                            ->notification-preferences
                                            ->role-warehouse-credentials ->searchable-user ->user ->warehouse-credentials prepare-user]]
            [minorodata.utils :refer [find-all-by-field find-by-field]]
            [mold-http.core :refer [reg-event-http]]
            [re-frame.core :as re-frame]
            [re-posh.core :as re-posh]
            [minorodata.app.utils :as utils]))


;; =============================================================================
;; Admin data
;; =============================================================================


(defn admin-data-members-request [_ _]
  (let [{cid :user/cid} @(re-posh/subscribe [:user/pull])]
    {:http-xhrio
     {:id  :admin-data-request
      :url (str AUTH_API_HOST "/clients/" cid "/users")}}))

(defn admin-data-members-callback [_ [error results]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to fetch admin data"]}


    (let [{cid :user/cid} @(re-posh/subscribe [:user/pull])
          users (->> results
                     (map (fn [u]
                            (utils/assoc-some
                             {:id         (:id u)
                              :user-name  (:user/name u)
                              :login-name (:user/email u)
                              :role       (:legacy/role u)
                              :is-sso     (:user/is-sso? u)
                              :is-blocked (= (:user/status u) :user.status/active)}
                             :role-id    (:user/role u)))))]
      {:dispatch [:client-data/update
                  [:client-data/cid cid]
                  (->client-data {:cid cid :users users})
                  {:replace true}]})))

(reg-event-http
 :users.api/fetch-admin-data-members
 admin-data-members-request
 admin-data-members-callback)

(defn admin-data-request [_ _]
  (let [current-user @(re-posh/subscribe [:user/pull])]
    {:http-xhrio
     {:id  :admin-data-request
      :url (str AUTH_API_HOST "/clients/" (:user/cid current-user))}}))

(defn admin-data-callback [{:keys [db]} [error results]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to fetch admin data"]}
    (let [raw-client-data {:cid        (:id results)
                           :clientname (:client/name results)
                           :hostname   (:client/name results)
                           :timezone   (:client/timezone results)}
          data    (->client-data raw-client-data)
          cid     (:cid raw-client-data)
          exists? @(re-posh/subscribe [:client-data/pull [:client-data/cid cid]])]
      {:db       (assoc-in db [:app :client-timezone] (:client/timezone results))
       :dispatch (if exists?
                   [:client-data/update [:client-data/cid cid] data]
                   [:client-data/add data])})))

(reg-event-http
 :users.api/fetch-admin-data
 admin-data-request
 admin-data-callback)


;; =============================================================================
;; Fetch client roles
;; =============================================================================

(defn get-roles-request [_ _]
  (let [current-user @(re-posh/subscribe [:user/pull])]
    {:http-xhrio
     {:id        :get-roles-request
      :url       (str AUTH_API_HOST "/roles")
      :params    {:client-id (:user/cid current-user)}
      :cb-params {:cid (:user/cid current-user)}}}))

(defn get-roles-callback [_ [error results {:keys [cid]}]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to fetch client roles"]}
    (let [data {:cid         cid
                :roles       (mapv (fn [r]
                                     {:role/id          (:id r)
                                      :role/name        (:role/name r)
                                      :role/permissions (set (mapv keyword (:role/permissions r)))})
                                   (:client-roles results))
                :permissions (mapv keyword (:all-permissions results))}]
      {:dispatch-n [[:client-roles/delete [:client-roles/cid (:cid data)]]
                    [:client-roles/add (->client-roles data)]]})))

(reg-event-http
 :users.api/fetch-roles
 get-roles-request
 get-roles-callback)

;; =============================================================================
;; Create client role
;; =============================================================================

(defn create-role-request [_ [params]]
  (let [{:keys [name permissions]} params
        {:user/keys [cid]} @(re-posh/subscribe [:user/pull])
        client-params {"client/id"        cid
                       "role/name"        name
                       "role/permissions" permissions}]
    {:http-xhrio
     {:id     :create-role-request
      :method :post
      :url    (str AUTH_API_HOST "/roles")
      :params client-params}}))


(defn create-role-callback [_ [error]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to create client role"]}
    {:dispatch-n [[:users.api/fetch-roles]
                  [:app.events/add-snack :success "Client role created"]]}))


(reg-event-http
 :users.api/create-role
 create-role-request
 create-role-callback)

;; =============================================================================
;; Update client role
;; =============================================================================

(defn update-role-request [_ [params]]
  (let [{:keys [id name permissions]} params
        {:user/keys [cid]} @(re-posh/subscribe [:user/pull])
        client-params {"client/id"        cid
                       "role/name"        name
                       "role/permissions" permissions}]
    {:http-xhrio
     {:id     :update-role-request
      :method :put
      :url    (str AUTH_API_HOST "/roles/" id)
      :params client-params}}))


(defn update-role-callback [_ [error]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to update client role"]}
    {:dispatch-n [[:users.api/fetch-roles]
                  [:app.events/add-snack :success "Client role updated"]]}))


(reg-event-http
 :users.api/update-role
 update-role-request
 update-role-callback)

;; =============================================================================
;; Notification preferences
;; =============================================================================

(defn convert-preferences [preferences]
  (let [all-slack-preferences       (find-all-by-field preferences :target-type "slack")
        any-slack-preference        (first all-slack-preferences)
        slack-webhook               (:target any-slack-preference)
        slack-extract-preferences   (find-all-by-field all-slack-preferences :source-type "EXTRACT")
        slack-extract-success       (some? (find-by-field slack-extract-preferences :status "success"))
        slack-extract-failure       (some? (find-by-field slack-extract-preferences :status "fail"))
        slack-transform-preferences (find-all-by-field all-slack-preferences :source-type "TRANSFORM")
        slack-transform-success     (some? (find-by-field slack-transform-preferences :status "success"))
        slack-transform-failure     (some? (find-by-field slack-transform-preferences :status "fail"))

        all-ms-teams-preferences       (find-all-by-field preferences :target-type "ms-teams")
        any-ms-teams-preference        (first all-ms-teams-preferences)
        ms-teams-webhook               (:target any-ms-teams-preference)
        ms-teams-webhook-workflows     (= (get-in any-ms-teams-preference [:extra :type]) "workflow-trigger")
        ms-teams-extract-preferences   (find-all-by-field all-ms-teams-preferences :source-type "EXTRACT")
        ms-teams-extract-success       (some? (find-by-field ms-teams-extract-preferences :status "success"))
        ms-teams-extract-failure       (some? (find-by-field ms-teams-extract-preferences :status "fail"))
        ms-teams-transform-preferences (find-all-by-field all-ms-teams-preferences :source-type "TRANSFORM")
        ms-teams-transform-success     (some? (find-by-field ms-teams-transform-preferences :status "success"))
        ms-teams-transform-failure     (some? (find-by-field ms-teams-transform-preferences :status "fail"))

        all-generic-preferences       (find-all-by-field preferences :target-type "generic")
        any-generic-preference        (first all-generic-preferences)
        generic-webhook               (:target any-generic-preference)
        generic-extract-preferences   (find-all-by-field all-generic-preferences :source-type "EXTRACT")
        generic-extract-success       (some? (find-by-field generic-extract-preferences :status "success"))
        generic-extract-failure       (some? (find-by-field generic-extract-preferences :status "fail"))
        generic-transform-preferences (find-all-by-field all-generic-preferences :source-type "TRANSFORM")
        generic-transform-success     (some? (find-by-field generic-transform-preferences :status "success"))
        generic-transform-failure     (some? (find-by-field generic-transform-preferences :status "fail"))]

    {:slack-webhook            slack-webhook
     :slack-transforms-success slack-transform-success
     :slack-transforms-failure slack-transform-failure
     :slack-extracts-success   slack-extract-success
     :slack-extracts-failure   slack-extract-failure

     :ms-teams-webhook            ms-teams-webhook
     :ms-teams-webhook-workflows  ms-teams-webhook-workflows
     :ms-teams-transforms-success ms-teams-transform-success
     :ms-teams-transforms-failure ms-teams-transform-failure
     :ms-teams-extracts-success   ms-teams-extract-success
     :ms-teams-extracts-failure   ms-teams-extract-failure

     :generic-webhook            generic-webhook
     :generic-transforms-success generic-transform-success
     :generic-transforms-failure generic-transform-failure
     :generic-extracts-success   generic-extract-success
     :generic-extracts-failure   generic-extract-failure}))


(defn get-notification-preferences-request [_ _]
  {:http-xhrio
   {:id  :get-notification-preferences-request
    :url "/api/notifications/preferences"}})

(defn get-notification-preferences-callback [_ [error results]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to fetch notification preferences"]}

    (let [preferences (:preferences results)
          data (convert-preferences preferences)]
      {:dispatch-n [[:notification-preferences/delete]
                    [:notification-preferences/add
                     (->notification-preferences
                      (assoc data :preferences preferences))]]})))

(reg-event-http
 :users.api/fetch-notification-preferences
 get-notification-preferences-request
 get-notification-preferences-callback)


;; =============================================================================
;; Create notification preference
;; =============================================================================


(defn post-notification-preference-request [_ [params]]
  {:http-xhrio
   {:id     :post-notification-preferences-request
    :method :post
    :params {:preferences [params]}
    :url    "/api/notifications/preferences"}})

(defn post-notification-preference-callback [_ [error _]]
  (when (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to create preference"]}))

(reg-event-http
 :users.api/create-notification-preference
 post-notification-preference-request
 post-notification-preference-callback)


;; =============================================================================
;; Update notification preference
;; =============================================================================


(defn put-notification-preference-request [_ [params]]
  {:http-xhrio
   {:id     :put-notification-preferences-request
    :method :put
    :params {:preference params}
    :url    "/api/notifications/preferences"}})

(defn put-notification-preference-callback [_ [error _]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to update preference"]}
    {:dispatch [:app.events/add-snack :success "Notification preference updated"]}))

(reg-event-http
 :users.api/update-notification-preference
 put-notification-preference-request
 put-notification-preference-callback)


;; =============================================================================
;; Delete notification preference
;; =============================================================================


(defn delete-notification-preference-request [_ [params]]
  {:http-xhrio
   {:id        :delete-notification-preferences-request
    :method    :delete
    :params    {:preference params}
    :url       "/api/notifications/preferences"
    :cb-params params}})

(defn delete-notification-preference-callback [_ [error _ {:keys [preference-id]}]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to delete preference"]}
    {:dispatch-n [[:app.events/add-snack :success "Notification preference deleted"]
                  [:notification/delete [:notification/preference-id preference-id]]]}))

(reg-event-http
 :users.api/delete-notification-preference
 delete-notification-preference-request
 delete-notification-preference-callback)


;; =============================================================================
;; Update notification preferences
;; Prerequisite — notification preferences should already be fetched, otherwise
;; there's a risk of not removing some of the entries, thus not applying the
;; changeset properly.
;; =============================================================================


(defn create-notification-slack-update-actions [{:keys [checked? source-type status slack-webhook preferences]}]
  (let [filtered-preferences (-> preferences
                                 (find-all-by-field :target-type "slack")
                                 (find-all-by-field :source-type source-type)
                                 (find-all-by-field :status status))]
    (if checked?
      ;; Enable source type and status
      (if (empty? filtered-preferences)
        ;; Need to create new preference entry
        [[:users.api/create-notification-preference
          {:status      status
           :source-type source-type
           :target-type "slack"
           :target      slack-webhook
           :source      ""}]]
        ;; Need to update existing entries
        (mapv (fn [preference]
                [:users.api/update-notification-preference
                 {:preference-id (:preference-id preference)
                  :status        status
                  :source-type   source-type
                  :target-type   "slack"
                  :target        slack-webhook
                  :source        ""}])
              filtered-preferences))

      ;; Disable source type and status
      (when-not (empty? filtered-preferences)
        (mapv (fn [preference]
                [:users.api/delete-notification-preference
                 {:preference-id (:preference-id preference)}])
              filtered-preferences)))))

(defn create-notification-teams-update-actions [{:keys [checked? source-type status ms-teams-webhook preferences]}]
  (let [filtered-preferences (-> preferences
                                 (find-all-by-field :target-type "ms-teams")
                                 (find-all-by-field :source-type source-type)
                                 (find-all-by-field :status status))]

    (if checked?
      ;; Enable source type and status
      (if (empty? filtered-preferences)
        ;; Need to create new preference entry
        [[:users.api/create-notification-preference
          {:status      status
           :source-type source-type
           :target-type "ms-teams"
           :target      ms-teams-webhook
           :source      ""}]]
        ;; Need to update existing entries
        (mapv (fn [preference]
                [:users.api/update-notification-preference
                 {:preference-id (:preference-id preference)
                  :status        status
                  :source-type   source-type
                  :target-type   "ms-teams"
                  :target        ms-teams-webhook
                  :source        ""}])
              filtered-preferences))

      ;; Disable source type and status
      (when-not (empty? filtered-preferences)
        (mapv (fn [preference]
                [:users.api/delete-notification-preference
                 {:preference-id (:preference-id preference)}])
              filtered-preferences)))))


(re-frame/reg-event-fx
 :users.events/apply-notification-preferences
 (fn [_ [_ new-preferences-form-data]]
   (let [notification-preferences @(re-posh/subscribe [:notification-preferences/pull])

         current-preferences (:notification-preferences/preferences notification-preferences)

         full-data (merge (->> notification-preferences
                               (map (fn [[k v]] [(-> k name keyword) v]))
                               (into {}))
                          new-preferences-form-data)

         {:keys [slack-transforms-success slack-transforms-failure
                 slack-extracts-success slack-extracts-failure
                 slack-webhook

                 ms-teams-webhook
                 ms-teams-transforms-success ms-teams-transforms-failure
                 ms-teams-extracts-success ms-teams-extracts-failure]}
         full-data

          ;here
         actions (concat
                  (create-notification-slack-update-actions
                   {:checked?      slack-transforms-success
                    :source-type   "TRANSFORM"
                    :status        "success"
                    :slack-webhook slack-webhook
                    :preferences   current-preferences})

                  (create-notification-slack-update-actions
                   {:checked?      slack-transforms-failure
                    :source-type   "TRANSFORM"
                    :status        "fail"
                    :slack-webhook slack-webhook
                    :preferences   current-preferences})

                  (create-notification-slack-update-actions
                   {:checked?      slack-extracts-success
                    :source-type   "EXTRACT"
                    :status        "success"
                    :slack-webhook slack-webhook
                    :preferences   current-preferences})

                  (create-notification-slack-update-actions
                   {:checked?      slack-extracts-failure
                    :source-type   "EXTRACT"
                    :status        "fail"
                    :slack-webhook slack-webhook
                    :preferences   current-preferences})


                  (create-notification-teams-update-actions
                   {:checked?         ms-teams-transforms-success
                    :source-type      "TRANSFORM"
                    :status           "success"
                    :ms-teams-webhook ms-teams-webhook
                    :preferences      current-preferences})

                  (create-notification-teams-update-actions
                   {:checked?         ms-teams-transforms-failure
                    :source-type      "TRANSFORM"
                    :status           "fail"
                    :ms-teams-webhook ms-teams-webhook
                    :preferences      current-preferences})

                  (create-notification-teams-update-actions
                   {:checked?         ms-teams-extracts-success
                    :source-type      "EXTRACT"
                    :status           "success"
                    :ms-teams-webhook ms-teams-webhook
                    :preferences      current-preferences})

                  (create-notification-teams-update-actions
                   {:checked?         ms-teams-extracts-failure
                    :source-type      "EXTRACT"
                    :status           "fail"
                    :ms-teams-webhook ms-teams-webhook
                    :preferences      current-preferences})
                  [[:app.events/add-snack :success "Sucessfully updated notification preferences"]])]

     {:dispatch-n     actions
      :dispatch-later {:ms       1000
                       :dispatch [:users.api/fetch-notification-preferences]}})))

(defn get-preference [preferences target-type source-type status]
  (some-> preferences
          (find-all-by-field :target-type target-type)
          (find-all-by-field :source-type source-type)
          (find-all-by-field :status status)
          first))

(defn field-key [target-type source-type status]
  (keyword (str target-type
                (case source-type
                  "TRANSFORM" "-transforms"
                  "EXTRACT" "-extracts")
                (case status
                  "fail"    "-failure"
                  "success" "-success"))))

(defn create-preference [{:keys [data preference checked? target-type source-type status]}]
  (let [id     (:preference-id preference)
        target (get data (keyword (str target-type "-webhook")))
        type   (cond
                 (and (not id) checked?) "create"
                 (and id (or (not checked?) (empty? target))) "delete"
                 (and id checked?) "update")]
    (when (and target type)
      (cond-> {:type        type
               :target-type target-type
               :target      target
               :source-type source-type
               :status      status}
        (= target-type "ms-teams")
        (assoc :extra {:type (if (get data :ms-teams-webhook-workflows)
                               "workflow-trigger"
                               "legacy-webhook-trigger")})
        id (assoc :preference-id id)))))

(defn build-preferences [form-data notification-preferences]
  (let [preferences (:notification-preferences/preferences notification-preferences)
        full-data form-data
        get-preference (partial get-preference preferences)]
    (filter
     identity
     (into []
           (for [target-type ["slack" "ms-teams" "generic"]
                 source-type ["TRANSFORM" "EXTRACT"]
                 status      ["fail" "success"]]
             (let [checked? (get full-data (field-key target-type source-type status))]
               (create-preference {:data        form-data
                                   :checked?    checked?
                                   :preference  (get-preference target-type source-type status)
                                   :target-type target-type
                                   :source-type source-type
                                   :status      status})))))))

;; =============================================================================
;; Bulk update notification preferences
;; =============================================================================

(defn bulk-update-notification-preferences-request [_ [new-preferences-form-data]]
  (let [notification-preferences @(re-posh/subscribe [:notification-preferences/pull])
        preferences              (build-preferences new-preferences-form-data notification-preferences)]
    (when (seq preferences)
      {:http-xhrio
       {:id     :put-bulk-notification-preferences-request
        :method :put
        :params {:preferences preferences}
        :url    "/api/notifications/preferences/bulk"}})))

(defn bulk-update-notification-preferences-callback [_ [error _]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to update preferences"]}
    {:dispatch-n [[:users.api/fetch-notification-preferences]
                  [:app.events/add-snack :success "Preferences updated"]]}))

(reg-event-http
 :users.api/bulk-update-notification-preferences
 bulk-update-notification-preferences-request
 bulk-update-notification-preferences-callback)

;; =============================================================================
;; Create client
;; =============================================================================

(defn create-client-data-request [_ [params]]
  (let [{:keys [clientname host]} params
        client-params {:client-name clientname
                       :host        host
                       :cid         ""}]
    {:http-xhrio
     {:id     :create-client-request
      :method :post
      :url    "/api/admin-clients"
      :params client-params}}))


(defn create-client-data-callback [_ [error]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to create client data"]}
    {:dispatch [:users.api/fetch-admin-data]}))


(reg-event-http
 :users.api/create-client-data
 create-client-data-request
 create-client-data-callback)


;; =============================================================================
;; Update client
;; =============================================================================

(defn im-v2-update-client-data-request [_ [params]]
  (let [current-user @(re-posh/subscribe [:user/pull])
        cid (:user/cid current-user)
        {:keys [clientname timezone]} params]
    {:http-xhrio
     {:id     :update-client-request
      :method :patch
      :url    (str AUTH_API_HOST "/clients/" cid)
      :params {"client/name"     clientname
               "client/timezone" (or timezone "UTC")}}}))

(defn im-v2-update-client-data-callback [_ [error]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to update client data"]}
    {:dispatch-n [[:users.api/fetch-admin-data]
                  [:app.events/add-snack :success "Updated client data"]]}))

(reg-event-http
 :users.api/im-v2-update-client-data
 im-v2-update-client-data-request
 im-v2-update-client-data-callback)

(defn update-client-data-request [_ [params]]
  (let [current-user @(re-posh/subscribe [:user/pull])
        cid (:user/cid current-user)
        {:keys [clientname timezone]} params
        client-params {:client-name clientname
                       :cid         cid
                       :timezone    timezone}]
    {:http-xhrio
     {:id     :update-client-request
      :method :put
      :url    "/api/admin-clients"
      :params client-params}}))

(defn update-client-data-callback [_ [error]]
  {})


(reg-event-http
 :users.api/update-client-data
 update-client-data-request
 update-client-data-callback)


;; =============================================================================
;; Create client user
;; =============================================================================

(defn create-client-user-request [_ [{:keys [params on-close]}]]
  (let [{:keys [user-name login-name role password]} params
        current-user @(re-posh/subscribe [:user/pull])
        cid (:user/cid current-user)
        url (str AUTH_API_HOST "/users")

        user-params  {"user/name"           user-name
                      "user/email"          login-name
                      "user/password"       password
                      "legacy/role"         "user"
                      "user/role"           role
                      "client/id"           cid
                      "user/email-verified" true}]
    {:http-xhrio
     {:id        :create-client-user-request
      :method    :post
      :url       url
      :cb-params {:on-close   on-close
                  :login-name login-name}
      :params    user-params}}))


(defn create-client-user-callback [_ [error _ {:keys [on-close login-name]}]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to create a user"]}
    (do
      (on-close)
      {:dispatch-n [[:app.events/add-snack :success (str "Created user " login-name)]
                    [:users.api/fetch-admin-data-members]]})))


(reg-event-http
 :users.api/create-client-user
 create-client-user-request
 create-client-user-callback)


;; =============================================================================
;; Update client user
;; =============================================================================

(defn im-v2-update-client-user-request [_ [params]]
  (let [{:keys [user-name login-name role id]} params
        user-params {"user/name"   user-name
                     "user/email"  login-name
                     "user/role"   role}]
    {:http-xhrio
     {:id     :im-v2-update-client-user-request
      :method :patch
      :url    (str AUTH_API_HOST "/users/" id)
      :params user-params}}))

(defn im-v2-update-client-user-callback [_ [error]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to update user"]}
    {:dispatch [:users.api/fetch-admin-data-members]}))

(reg-event-http
 :users.api/im-v2-update-client-user
 im-v2-update-client-user-request
 im-v2-update-client-user-callback)

(defn update-client-user-request [_ [params]]
  (let [{:keys [user-name login-name role-name id]} params
        current-user @(re-posh/subscribe [:user/pull])
        cid (:user/cid current-user)
        user-params {:user-id    id
                     :user-name  user-name
                     :login-name login-name
                     :role       role-name
                     :cid        cid}]
    {:http-xhrio
     {:id     :update-client-user-request
      :method :put
      :url    "/api/admin/users"
      :params user-params}}))


(defn update-client-user-callback [_ [error]]
  {})


(reg-event-http
 :users.api/update-client-user
 update-client-user-request
 update-client-user-callback)


;; =============================================================================
;; Update client user password
;; =============================================================================

(defn im-v2-update-client-user-password-request [_ [{:keys [params on-close]}]]
  (let [{:keys [id password]} params]

    {:http-xhrio
     {:id        :im-v2-update-client-user-password-request
      :method    :put
      :url       (str AUTH_API_HOST "/users/" id "/password")
      :cb-params {:on-close on-close}
      :params    {"user/password" password}}}))

(defn im-v2-update-client-user-password-callback [_ [error _ {:keys [on-close]}]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to update the password"]}
    (do
      (on-close)
      {:dispatch [:users.api/fetch-admin-data-members]})))

(reg-event-http
 :users.api/im-v2-update-client-user-password
 im-v2-update-client-user-password-request
 im-v2-update-client-user-password-callback)

;=> Legacy
(defn update-client-user-password-request [_ [{:keys [params on-close]}]]
  (let [{:keys [user-name login-name role id password]} params
        current-user @(re-posh/subscribe [:user/pull])
        cid (:user/cid current-user)
        user-params {:user-id    id
                     :user-name  user-name
                     :login-name login-name
                     :password   password
                     :role       role
                     :cid        cid}]
    {:http-xhrio
     {:id        :update-client-user-password-request
      :method    :put
      :url       "/api/admin/users"
      :cb-params {:on-close on-close}
      :params    user-params}}))

(defn update-client-user-password-callback [_ [error _ {:keys [on-close]}]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to update the password"]}
    (do
      (on-close)
      {:dispatch [:users.api/fetch-admin-data]})))

(reg-event-http
 :users.api/update-client-user-password
 update-client-user-password-request
 update-client-user-password-callback)


;; =============================================================================
;; Delete client user
;; =============================================================================

(defn im-v2-delete-client-user-request [_ [user-id]]
  {:http-xhrio
   {:id     :im-v2-delete-client-user-request
    :method :delete
    :url    (str AUTH_API_HOST "/users/" user-id)}})

(defn im-v2-delete-client-user-callback [_ [error]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to delete user"]}
    {:dispatch [:users.api/fetch-admin-data-members]}))

(reg-event-http
 :users.api/im-v2-delete-client-user
 im-v2-delete-client-user-request
 im-v2-delete-client-user-callback)

(defn delete-client-user-request [_ [params]]
  (let [{:keys [id]} params
        user-params {:user-id id}]
    {:http-xhrio
     {:id     :delete-client-user-request
      :method :delete
      :url    "/api/admin/users"
      :params user-params}}))


(defn delete-client-user-callback [_ [error]]
  {})


(reg-event-http
 :users.api/delete-client-user
 delete-client-user-request
 delete-client-user-callback)


;; =============================================================================
;; Login
;; =============================================================================

(defn login-request [_ [params]]
  (let [{:keys [username password]} params
        login-params {:user     username
                      :password password}]
    {:http-xhrio
     {:id     :login-request
      :method :post
      :url    "/api/login"
      :params login-params}}))

(defn login-callback [{:keys [db]} [error results]]
  (if (some? error)
    (let [{:keys [status]} error
          error-msg (case status
                      401 "Incorrect username or password."
                      403 "Too many wrong attempts. User is blocked, please contact your administrator."
                      429 "Too many wrong attempts. Please try again in a moment."
                      "Failed to login.")]
      {:dispatch [:app.events/add-snack :error error-msg]})

    (let [user (->user (prepare-user results))]
      {:db                     (update db :app merge {:user-id   (:user/uid user)
                                                      :client-id (:user/cid user)})
       :dispatch-n             [[:user/add user]
                                [:sentry/set-user {:email (:user/user user)}]]
       :persist-user           user

       :unleash/update-context {:userId    (:user/user user)
                                :client-id (:user/cid user)}})))

(reg-event-http
 :users.api/login-request
 login-request
 login-callback
 :authenticate? false)

;; =============================================================================
;; Fetch searchable users
;; =============================================================================

(defn fetch-searchable-users-request [_]
  {:http-xhrio
   {:id  :searchable-users-fetch-request
    :url "/api/transformations/searchable-users"}})

(defn fetch-searchable-users-callback [_ [error resp]]
  (if (some? error)
    {:dispatch [:app.api/log :error "Failed to load searchable users" error]}

    (let [users (:results resp)]
      {:dispatch [:searchable-user/add-many (mapv ->searchable-user users)]})))

(reg-event-http
 :users.api/fetch-searchable-users
 fetch-searchable-users-request
 fetch-searchable-users-callback)

;; =============================================================================
;; Set user warehouse credentials
;; =============================================================================

(defn update-user-warehouse-credentials-request [_ [{:keys [user-id credentials enforce-credentials]}]]
  (let [user @(re-posh/subscribe [:user/pull])
        user-id (or user-id (:user/uid user))]
    {:http-xhrio
     {:id        :update-user-warehouse-credentials-request
      :method    :post
      :url       (str "/api/user/" user-id "/warehouse-credentials")
      :cb-params {:user-id user-id}
      :params    credentials}}))

(defn update-user-warehouse-credentials-callback [_ [error resp {:keys [user-id]}]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to update credentials"]}
    (let [user @(re-posh/subscribe [:user/pull])]
      {:dispatch-n (cond-> [[:app.events/add-snack :success "Updated warehouse credentials"]
                            [:users.api/fetch-warehouse-credentials {:user-id user-id}]
                            [:connections.api/fetch-warehouse-status {:user-id user-id}]]
                     (= user-id (:user/uid user))
                     (concat [[:sql-metadata/delete]]))})))

(reg-event-http
 :users.api/update-warehouse-credentials
 update-user-warehouse-credentials-request
 update-user-warehouse-credentials-callback)

;; =============================================================================
;; Fetch user warehouse credentials
;; =============================================================================

(defn fetch-user-warehouse-credentials-request [_ [{:keys [user-id]}]]
  (let [user    @(re-posh/subscribe [:user/pull])
        user-id (or user-id (:user/uid user))]
    {:http-xhrio
     {:id        :fetch-user-warehouse-credentials-request
      :method    :get
      :url       (str "/api/user/" user-id "/warehouse-credentials")
      :cb-params {:user-id user-id}}}))

(defn fetch-user-warehouse-credentials-callback [_ [error resp {:keys [user-id]}]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to fetch credentials"]}
    {:dispatch-n [[:warehouse-credentials/delete [:warehouse-credentials/user-id user-id]]
                  [:warehouse-credentials/add (->warehouse-credentials {:user-id             user-id
                                                                        :credentials         (:credentials resp)
                                                                        :enforce-credentials (or (:enforce-credentials resp) false)})]]}))

(reg-event-http
 :users.api/fetch-warehouse-credentials
 fetch-user-warehouse-credentials-request
 fetch-user-warehouse-credentials-callback)


;; =============================================================================
;; Fetch role warehouse credentials
;; =============================================================================

(defn fetch-role-warehouse-credentials-request [_ [{:keys [role-id]}]]
  {:http-xhrio
   {:id        :fetch-role-warehouse-credentials-request
    :method    :get
    :url       (str "/api/role/" role-id "/warehouse-credentials")
    :cb-params {:role-id role-id}}})

(defn fetch-role-warehouse-credentials-callback [_ [error resp {:keys [role-id]}]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to fetch role credentials"]}
    {:dispatch-n [[:role-warehouse-credentials/delete [:role-warehouse-credentials/role-id role-id]]
                  [:role-warehouse-credentials/add
                   (->role-warehouse-credentials {:role-id     role-id
                                                  :credentials (:credentials resp)})]]}))

(reg-event-http
 :users.api/fetch-role-warehouse-credentials
 fetch-role-warehouse-credentials-request
 fetch-role-warehouse-credentials-callback)

;; =============================================================================
;; Set role warehouse credentials
;; =============================================================================

(defn update-role-warehouse-credentials-request [_ [{:keys [role-id credentials]}]]
  {:http-xhrio
   {:id        :update-role-warehouse-credentials-request
    :method    :post
    :url       (str "/api/role/" role-id "/warehouse-credentials")
    :cb-params {:role-id role-id}
    :params    credentials}})

(defn update-role-warehouse-credentials-callback [_ [error resp {:keys [role-id]}]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to update role credentials"]}
    {:dispatch-n (cond-> [[:app.events/add-snack :success "Updated warehouse credentials"]
                          [:users.api/fetch-role-warehouse-credentials {:role-id role-id}]
                          [:connections.api/fetch-warehouse-status {:role-id role-id}]])}))

(reg-event-http
 :users.api/update-role-warehouse-credentials
 update-role-warehouse-credentials-request
 update-role-warehouse-credentials-callback)

(defn fetch-user-preferences-request []
  {:http-xhrio
   {:id     :fetch-user-preferences-request
    :method :get
    :url    "/api/user/preferences"}})

(defn fetch-user-preferences-callback [{:keys [db]} [error resp]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to fetch user preferences"]}
    {:db (assoc-in db [:user :preferences] (:preferences resp))}))

(reg-event-http
 :users.api/fetch-user-preferences
 fetch-user-preferences-request
 fetch-user-preferences-callback)


(defn update-user-preferences-request [_ [{:keys [preferences]}]]
  {:http-xhrio
   {:id     :update-user-preferences-request
    :method :PATCH
    :url    "/api/user/preferences"
    :params preferences}})

(defn update-user-preferences-callback [{:keys [db]} [error resp]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to update user preferences"]}
    {:db (assoc-in db [:user :preferences] (:preferences resp))}))

(reg-event-http
 :users.api/update-user-preferences
 update-user-preferences-request
 update-user-preferences-callback)
